if (!customElements.get('ed-companion-bundle')) {
  customElements.define('ed-companion-bundle', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      try { this.data = JSON.parse(this.querySelector('[data-bundle-data]').textContent); } catch { return; }
      this.cartMode = this.dataset.mode === 'cart';
      this.root = window.Shopify?.routes?.root || '/';
      this.form = document.getElementById(this.dataset.formId);
      this.select = this.querySelector('[data-bundle-variant]');
      this.button = this.querySelector('[data-bundle-add]');
      this.status = this.querySelector('[data-bundle-status]');
      this.cartLink = this.querySelector('[data-bundle-cart]');
      this.money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.data.currency }).format(cents / 100);
      const signal = this.controller.signal;
      this.select.addEventListener('change', event => { if (this.cartMode) event.stopPropagation(); this.renderPrice(); }, { signal });
      this.button.addEventListener('click', () => this.add(), { signal });
      this.section = this.closest('.shopify-section') || document;
      this.section.addEventListener('change', () => queueMicrotask(() => this.sync()), { signal });
      const variantInput = this.form?.querySelector('[name="id"]');
      this.observer = new MutationObserver(() => this.sync());
      if (variantInput) this.observer.observe(variantInput, { attributes: true, attributeFilter: ['value'] });
      this.sync();
    }
    disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); this.controller = null; }
    setText(selector, text) { this.querySelector(selector).textContent = text || ''; }
    sync() {
      if (this.busy) return;
      const id = String(this.cartMode ? this.dataset.sourceVariant : this.form?.querySelector('[name="id"]')?.value || '');
      const source = this.data.source.variants.find(v => v.id === id && v.available);
      const offer = this.data.offers.find(o => (!this.cartMode || o.id === this.dataset.offerId) && o.sourceVariantIds?.includes(id) && o.companion?.variants?.some(v => v.available && o.companionVariantIds.includes(v.id)));
      this.hidden = !source || !offer;
      if (this.hidden) return;
      const previous = this.offer?.id === offer.id ? this.select.value : null;
      this.source = source; this.offer = offer;
      if (!this.cartMode) {
        this.setText('[data-bundle-source-title]', this.data.source.title);
        this.setText('[data-bundle-source-price]', this.money(source.price));
        this.querySelector('[data-bundle-source-image]').src = this.data.source.image;
      }
      this.querySelector('[data-bundle-companion-image]').src = offer.companion.image;
      this.querySelector('[data-bundle-companion-link]').href = offer.companion.url;
      this.querySelector('[data-bundle-companion-link]').setAttribute('aria-label', offer.companion.title);
      this.setText('[data-bundle-companion-title]', this.cartMode ? offer.companion.shortTitle || offer.companion.title : offer.companion.title);
      this.select.replaceChildren(...offer.companion.variants.filter(v => v.available && offer.companionVariantIds.includes(v.id)).map(v => new Option(v.title === 'Default Title' ? offer.companion.title : v.title, v.id)));
      if ([...this.select.options].some(o => o.value === previous)) this.select.value = previous;
      this.select.closest('label').hidden = this.select.options.length === 1;
      this.renderPrice();
    }
    renderPrice() {
      const v = this.offer?.companion.variants.find(x => x.id === this.select.value);
      if (!v) return;
      const currencyDigits = new Intl.NumberFormat('en', { style: 'currency', currency: this.data.currency }).resolvedOptions().maximumFractionDigits;
      const unit = currencyDigits === 0 ? 100 : 1;
      const saving = Math.floor(v.price * 20 / (100 * unit)) * unit;
      this.setText('[data-bundle-original-price]', this.money(v.price));
      this.setText('[data-bundle-discount-price]', this.money(v.price - saving));
      this.setText('[data-bundle-total]', this.money((this.cartMode ? 0 : this.source.price) + v.price - saving));
    }
    async request(path, body) {
      const response = await fetch(this.root + path, { method: body ? 'POST' : 'GET', credentials: 'same-origin', headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) }, ...(body ? { body: JSON.stringify(body) } : {}) });
      const data = await response.json();
      if (!response.ok || data.status >= 400) throw new Error(data.description || this.data.text.error);
      return data;
    }
    operation() {
      if (this.pendingOperation) return this.pendingOperation;
      const key = 'ed-companion-pending:' + (this.cartMode ? 'cart:' : 'pair:') + location.pathname;
      let saved;
      try { saved = JSON.parse(sessionStorage.getItem(key) || 'null'); } catch { /* Storage can be unavailable. */ }
      const sourceProperties = this.form ? Object.fromEntries([...new FormData(this.form)].flatMap(([name, value]) => {
        const match = name.match(/^properties\[(.+)\]$/);
        return match && typeof value === 'string' && !match[1].startsWith('_epic_bundle_') ? [[match[1], value]] : [];
      })) : {};
      const operation = saved || { sourceProperties, id: crypto.randomUUID(), sourceId: this.source.id, companionId: this.select.value, code: this.offer.code, offerId: this.offer.id, key };
      this.pendingOperation = operation;
      try { sessionStorage.setItem(key, JSON.stringify(operation)); } catch { /* The in-memory guard remains active. */ }
      return operation;
    }
    clearOperation(op) { try { sessionStorage.removeItem(op.key); } catch { /* Optional storage. */ } this.pendingOperation = null; }
    async add() {
      if (this.busy || window.edCompanionBusy) return;
      this.sync();
      if (this.hidden || !this.source || !this.select.value) return;
      const fullCartOrigin = !!this.closest?.('#main-cart-footer');
      const noticeContext = this.closest?.('cart-drawer') ? 'drawer' : 'page';
      const selection = { sourceId: this.source.id, companionId: this.select.value, code: this.offer.code };
      window.edCompanionBusy = true;
      this.busy = true; this.button.disabled = true; this.select.disabled = true;
      this.status.textContent = this.data.text.adding; this.cartLink.hidden = true;
      const op = this.operation();
      try {
        let cart = await this.request('cart.js');
        let owned = cart.items.filter(i => i.properties?._epic_bundle_operation === op.id);
        const sameSelection = op.sourceId === selection.sourceId && op.companionId === selection.companionId && op.code === selection.code;
        if (!sameSelection && !owned.length) {
          this.clearOperation(op);
          throw new Error(this.data.text.selectionChanged);
        }
        if (this.cartMode) {
          const anchor = cart.items.some(i => String(i.variant_id) === op.sourceId && i.quantity > 0);
          const alreadyDiscounted = cart.discount_codes?.some(d => d.applicable && d.code.toUpperCase().startsWith('EDPAIR-'));
          const companionPresent = cart.items.some(i => this.offer.companion.variants.some(v => v.id === String(i.variant_id)) && i.properties?._epic_bundle_operation !== op.id);
          if (!anchor || (!owned.length && (alreadyDiscounted || companionPresent))) {
            if (!owned.length) this.clearOperation(op);
            throw new Error(this.data.text.cartChanged);
          }
        }
        const addingIds = this.cartMode ? [op.companionId] : [op.sourceId, op.companionId];
        if (!owned.length) {
          try {
            await this.request('cart/add.js', { items: addingIds.map(id => ({ id, quantity: 1, properties: { ...(id === op.sourceId ? op.sourceProperties : {}), _epic_bundle_operation: op.id, _epic_bundle_offer: op.offerId } })) });
          } catch (error) {
            cart = await this.request('cart.js');
            owned = cart.items.filter(i => i.properties?._epic_bundle_operation === op.id);
            if (!owned.length) { this.clearOperation(op); throw error; }
          }
          cart = await this.request('cart.js');
          owned = cart.items.filter(i => i.properties?._epic_bundle_operation === op.id);
        }
        if (owned.length !== addingIds.length || !addingIds.every(id => owned.some(i => String(i.variant_id) === id && i.quantity === 1))) {
          for (let remaining = owned.length; remaining > 0; remaining--) {
            const current = await this.request('cart.js');
            const line = current.items.find(item => item.properties?._epic_bundle_operation === op.id);
            if (!line) break;
            await this.request('cart/change.js', { id: line.key, quantity: 0 });
          }
          const check = await this.request('cart.js');
          if (check.items.some(i => i.properties?._epic_bundle_operation === op.id)) throw new Error(this.data.text.retry);
          this.clearOperation(op); throw new Error(this.data.text.unavailable);
        }
        if (!Array.isArray(cart.discount_codes)) throw new Error(this.data.text.retry);
        const codes = [...new Set(cart.discount_codes.map(d => d.code).filter(c => c && !c.toUpperCase().startsWith('EDPAIR-')).concat(op.code))];
        const drawer = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const footer = document.getElementById('main-cart-footer');
        const sections = [...new Set([...(drawer?.getSectionsToRender?.().map(s => s.id) || ['cart-icon-bubble']), ...(footer ? [footer.dataset.id] : [])])].slice(0,5);
        const result = await this.request('cart/update.js', { discount: codes.join(','), sections, sections_url: location.pathname });
        const verified = await this.request('cart.js');
        const codeApplies = verified.discount_codes?.some(d => d.code.toUpperCase() === op.code.toUpperCase() && d.applicable);
        const discountedCompanion = verified.items.some(i => String(i.variant_id) === op.companionId && i.line_level_discount_allocations?.some(a => a.amount > 0));
        const outcome = !sameSelection ? this.data.text.previousPair : codeApplies && discountedCompanion ? this.data.text.added : this.data.text.promotion;
        if (this.cartMode) {
          this.status.textContent = '';
          window.edCompanionNotice = { text: outcome, context: noticeContext, announced: false };
        } else this.status.textContent = outcome;
        this.cartLink.hidden = false;
        this.clearOperation(op);
        try {
        if (!fullCartOrigin && drawer?.renderContents && result.sections && Object.values(result.sections).every(Boolean)) {
          if (!this.cartMode) drawer.setActiveElement?.(this.button);
          drawer.classList.remove('is-empty');
          drawer.renderContents(result);
        }
        if (fullCartOrigin && result.sections?.['cart-icon-bubble']) {
          const icon = document.getElementById('cart-icon-bubble');
          const html = new DOMParser().parseFromString(result.sections['cart-icon-bubble'], 'text/html');
          const content = html.querySelector('.shopify-section');
          if (icon && content) icon.innerHTML = content.innerHTML;
        }
        if (footer && result.sections?.[footer.dataset.id]) {
          const html = new DOMParser().parseFromString(result.sections[footer.dataset.id], 'text/html');
          const source = html.querySelector('#main-cart-footer .js-contents');
          if (source && footer.querySelector('.js-contents')) footer.querySelector('.js-contents').innerHTML = source.innerHTML;
          if (this.cartMode) document.getElementById('checkout')?.focus();
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ed-companion-bundle', cartData: result });
        document.querySelectorAll('ed-bundle-cart-status').forEach(status => status.refresh?.());
        } catch { /* The cart is confirmed; optional drawer rendering cannot change that result. */ }
      } catch (error) { this.status.textContent = error.message || this.data.text.error; this.cartLink.hidden = false; }
      finally { window.edCompanionBusy = false; this.busy = false; this.button.disabled = false; this.select.disabled = false; this.sync(); }
    }
  });
}

if (!customElements.get('ed-bundle-cart-status')) {
  customElements.define('ed-bundle-cart-status', class extends HTMLElement {
    connectedCallback() {
      this.refresh();
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
          if (event.source !== 'ed-companion-bundle') { window.edCompanionNotice = null; this.refresh(); }
        });
      }
    }
    disconnectedCallback() { this.unsubscribe?.(); }
    refresh() {
      const notice = window.edCompanionNotice;
      const context = this.closest('cart-drawer') ? 'drawer' : 'page';
      this.hidden = !notice || notice.context !== context;
      if (this.hidden) { this.textContent = ''; return; }
      if (this.textContent === notice.text) return;
      this.setAttribute('aria-live', notice.announced ? 'off' : 'polite');
      this.textContent = notice.text;
      notice.announced = true;
    }
  });
}
